import { featureFitsCondition } from '@datum-group/datum-map/lib/Layer/StyleUtils/features';
import {
  LayerConditionalStyle,
  LayerStyle,
} from '@datum-group/datum-map/lib/Layer/StyleUtils/resources';
import { createInRangeExpression, createStyles } from '@project/utils/map';
import { Feature, Map } from 'ol';
import { MultiPolygon, Polygon } from 'ol/geom';
import {
  CartogramLegendSettings,
  CartogramStyleSettings,
  FeaturesLegends,
} from './types';

export const createCartogramGeometry = (
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
) => {
  if (geometry.type === 'MultiPolygon')
    return new MultiPolygon(geometry.coordinates);

  return new Polygon(geometry.coordinates);
};

export const createCartogramFeatures = (geoJsonFeatures: GeoJSON.Feature[]) => {
  const features: Feature[] = [];
  geoJsonFeatures.forEach(({ geometry: geoJsonGeometry, properties, id }) => {
    if (
      geoJsonGeometry.type === 'Polygon' ||
      geoJsonGeometry.type === 'MultiPolygon'
    ) {
      const geometry = createCartogramGeometry(geoJsonGeometry);
      const cartogramFeature = new Feature({
        ...properties,
        geometry,
      });
      cartogramFeature.setId(id);
      features.push(cartogramFeature);
    }
  });

  return features;
};

export const createCartogramConditionalStyle = (
  { description, maxValue, minValue, color }: CartogramLegendSettings,
  computeColumn?: string,
  displayedText?: string,
): LayerConditionalStyle => ({
  id: description,
  name: description,
  style: createStyles({ polygonColor: color, text: displayedText }),
  expressions: computeColumn
    ? createInRangeExpression(minValue, maxValue, computeColumn)
    : [],
});

export const createCartogramStyle = ({
  legendsSettings,
  columnName,
  displayedText,
}: CartogramStyleSettings = {}): LayerStyle => ({
  default: createStyles({ text: displayedText }),
  conditional:
    legendsSettings?.map((legendSettings) =>
      createCartogramConditionalStyle(
        legendSettings,
        columnName,
        displayedText,
      ),
    ) ?? [],
});

export const getFeatureLegend = (
  feature: Feature,
  legendsSettings: CartogramLegendSettings[],
  computeColumn: string,
  map: Map,
) => {
  const conditions = legendsSettings.map((legendSettings) =>
    createCartogramConditionalStyle(legendSettings, computeColumn),
  );
  const featureStyleCondition = conditions.find((conditionStyle) =>
    conditionStyle.expressions.every((condition) =>
      featureFitsCondition(feature, condition, map),
    ),
  );
  if (!featureStyleCondition) return;

  return legendsSettings.find(
    ({ description }) => description === featureStyleCondition.name,
  );
};

export const getFeaturesLegends = (
  features: Feature[],
  legendsSettings: CartogramLegendSettings[],
  computeColumn: string,
  map: Map,
) =>
  features.reduce<FeaturesLegends>((acc, feature) => {
    const featureId = feature.getId();
    const legend = getFeatureLegend(
      feature,
      legendsSettings,
      computeColumn,
      map,
    );

    if (featureId && legend)
      acc[featureId] = { feature, legend, computeColumn };

    return acc;
  }, {});
